import React from 'react';
import Button from './Button';

interface PaginationProps {
    currentPage: number;
    lastPage: number;
    onPageChange: (page: number) => void;
    className?: string;
}

export default function Pagination({
    currentPage,
    lastPage,
    onPageChange,
    className = '',
}: PaginationProps) {
    // Generate page numbers to display
    const getPageNumbers = () => {
        const delta = 1; // How many pages to show around current page
        const range = [];
        const rangeWithDots = [];
        let l;

        range.push(1);

        if (lastPage <= 1) return [1];

        for (let i = currentPage - delta; i <= currentPage + delta; i++) {
            if (i < lastPage && i > 1) {
                range.push(i);
            }
        }

        // for showing last page number, user can click on it to go to last page
        // if (lastPage > 1) range.push(lastPage);

        for (const i of range) {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        }

        return rangeWithDots;
    };

    const pages = getPageNumbers();

    return (
        <div className={`flex items-center justify-center gap-2 ${className}`}>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="hidden sm:flex"
            >
                Previous
            </Button>
            {/* Mobile simplistic previous */}
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="sm:hidden p-2 border border-gray-300 rounded-md disabled:opacity-50"
                aria-label="Previous Page"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>

            <div className="flex items-center gap-1">
                {pages.map((page, index) => (
                    <React.Fragment key={index}>
                        {page === '...' ? (
                            <span className="px-3 py-1 text-gray-400">...</span>
                        ) : (
                            <button
                                onClick={() => onPageChange(page as number)}
                                className={`
                  min-w-[40px] h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors
                  ${currentPage === page
                                        ? 'bg-primary-600 text-white shadow-sm'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                    }
                `}
                            >
                                {page}
                            </button>
                        )}
                    </React.Fragment>
                ))}
            </div>

            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= lastPage}
                className="hidden sm:flex"
            >
                Next
            </Button>
            {/* Mobile simplistic next */}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= lastPage}
                className="sm:hidden p-2 border border-gray-300 rounded-md disabled:opacity-50"
                aria-label="Next Page"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
        </div>
    );
}
